import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_SEARCH_ITEMS } from '../../product-router/product';
import { useProductContext } from '../../product-router';
import { APIStates } from '../product-state-machine-types';
var useDebouncedState = function useDebouncedState(_ref) {
  var debounceTime = _ref.debounceTime;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    triggerDebounce = _useState2[0],
    setTriggerDebounce = _useState2[1];
  var debouncedSetter = useMemo(function () {
    return debounce(function () {
      setTriggerDebounce(true);
    }, debounceTime);
  }, [debounceTime, setTriggerDebounce]);
  return [triggerDebounce, setTriggerDebounce, debouncedSetter];
};
export var usePostQueryInvocation = function usePostQueryInvocation(_ref2) {
  var debounceTime = _ref2.debounceTime,
    query = _ref2.query,
    analyticsInfo = _ref2.analyticsInfo,
    filters = _ref2.filters,
    workspaces = _ref2.workspaces,
    sectionIds = _ref2.sectionIds,
    isDisplayed = _ref2.isDisplayed,
    postQueryItemSupplier = _ref2.postQueryItemSupplier,
    setPostQueryItems = _ref2.setPostQueryItems,
    setPostQueryAPIState = _ref2.setPostQueryAPIState,
    preventDebouncedSearch = _ref2.preventDebouncedSearch,
    isPreQueryFiltersEnabled = _ref2.isPreQueryFiltersEnabled;
  // Store the previous isDisplayed for this product. It is initialised to
  // account for the case where the product is initially displayed. In this
  // case, we want to fire the postQueryItemSupplier immediately.
  var previousIsDisplayed = useRef(false);

  // Store the previous query. The default here is an undefined string here to
  // support the case where the initial state of the component is a non-empty
  // string. In this case, we want to fire the postQueryItemSupplier
  // immediately.
  var previousQuery = useRef(undefined);
  var previousFilters = useRef(filters);
  var _useDebouncedState = useDebouncedState({
      debounceTime: debounceTime
    }),
    _useDebouncedState2 = _slicedToArray(_useDebouncedState, 3),
    triggerDebounce = _useDebouncedState2[0],
    setTriggerDebounce = _useDebouncedState2[1],
    debouncedSetter = _useDebouncedState2[2];
  useResetPostQueryAPIStateOnEmptySearch({
    query: query,
    filters: filters,
    setPostQueryAPIState: setPostQueryAPIState,
    isDisplayed: isDisplayed,
    isPreQueryFiltersEnabled: isPreQueryFiltersEnabled
  });
  useResetPostQueryItemOnNewSearch({
    query: query,
    filters: filters,
    setPostQueryItems: setPostQueryItems,
    setPostQueryAPIState: setPostQueryAPIState,
    isDisplayed: isDisplayed,
    debouncedSetter: debouncedSetter,
    sectionIds: sectionIds,
    isPreQueryFiltersEnabled: isPreQueryFiltersEnabled
  });
  useEffect(function () {
    var queryChanged = query !== previousQuery.current;
    var filtersChanged = !isEqual(previousFilters.current, filters);
    var isDisplayedChanged = previousIsDisplayed.current === false && isDisplayed;
    var search = query || isPreQueryFiltersEnabled && filters.length;
    if (preventDebouncedSearch && search && isDisplayed) {
      if (queryChanged) {
        previousQuery.current = query;
        setPostQueryAPIState(APIStates.Typing);
      }
      if (isDisplayedChanged || filtersChanged) {
        previousFilters.current = filters;
        postQueryItemSupplier({
          query: query,
          filters: filters,
          analyticsInfo: analyticsInfo,
          sectionIds: sectionIds,
          workspaces: workspaces
        }).catch(function (ignore) {});
      }
    } else if (
    // Fire when debounce is finished or when product becomes active
    (triggerDebounce || isDisplayedChanged) && (
    // Fire when search parameters change or when product becomes active
    queryChanged || filtersChanged) && search && isDisplayed && sectionIds) {
      previousQuery.current = query;
      previousFilters.current = filters;
      postQueryItemSupplier({
        query: query,
        filters: filters,
        analyticsInfo: analyticsInfo,
        sectionIds: sectionIds,
        workspaces: workspaces
      }).catch(function (ignore) {});
      setTriggerDebounce(false);
    }
    previousIsDisplayed.current = isDisplayed;

    // In the case of an empty query /  no filters-only search, we want to reset any previous query caches
    // Still potentially not optimal as you would not get the cache behaviour if you
    // clear the query when you're on a different tab. It shouldn't feel jank though.
    if (!search) {
      previousQuery.current = '';
      previousFilters.current = [];
    }
  }, [triggerDebounce, setTriggerDebounce, postQueryItemSupplier, sectionIds, query, analyticsInfo, filters, isDisplayed, workspaces, preventDebouncedSearch, isPreQueryFiltersEnabled, setPostQueryAPIState]);
};
var useResetPostQueryItemOnNewSearch = function useResetPostQueryItemOnNewSearch(_ref3) {
  var query = _ref3.query,
    filters = _ref3.filters,
    isDisplayed = _ref3.isDisplayed,
    setPostQueryItems = _ref3.setPostQueryItems,
    setPostQueryAPIState = _ref3.setPostQueryAPIState,
    debouncedSetter = _ref3.debouncedSetter,
    sectionIds = _ref3.sectionIds,
    preventDebouncedSearch = _ref3.preventDebouncedSearch,
    isPreQueryFiltersEnabled = _ref3.isPreQueryFiltersEnabled;
  // We want to retain the last search query value to avoid an infinite loop of postQueryItemSupplier
  var queryRef = useRef(query);
  var filtersRef = useRef(filters);
  useEffect(function () {
    if ((queryRef.current !== query || !isEqual(filtersRef.current, filters)) && (query || isPreQueryFiltersEnabled && filters.length) && isDisplayed && sectionIds && sectionIds.length > 0) {
      // reset post query items when we start a new search
      setPostQueryItems(EMPTY_SEARCH_ITEMS);
      // If debounced search is prevented we set the state to typing instead of loading
      if (preventDebouncedSearch) {
        // Typing state means that we don't fire the supplier on debounce and the user types their query
        setPostQueryAPIState(APIStates.Typing);
      } else {
        setPostQueryAPIState(APIStates.Loading);
      }
      debouncedSetter();
    }
    queryRef.current = query;
    filtersRef.current = filters;
  }, [query, isDisplayed, sectionIds, setPostQueryItems, setPostQueryAPIState, debouncedSetter, queryRef, filters, filtersRef, preventDebouncedSearch, isPreQueryFiltersEnabled]);
};
var useResetPostQueryAPIStateOnEmptySearch = function useResetPostQueryAPIStateOnEmptySearch(_ref4) {
  var query = _ref4.query,
    filters = _ref4.filters,
    isDisplayed = _ref4.isDisplayed,
    setPostQueryAPIState = _ref4.setPostQueryAPIState,
    preventDebouncedSearch = _ref4.preventDebouncedSearch,
    isPreQueryFiltersEnabled = _ref4.isPreQueryFiltersEnabled;
  // We want to retain the last search query value to avoid an infinite loop of postQueryItemSupplier
  var queryRef = useRef(query);
  var filtersRef = useRef(filters);
  useEffect(function () {
    // These two checks both assume query === '' so it's checked independanctly in the if condition below
    var queryRemoved = queryRef.current !== '' && (!isPreQueryFiltersEnabled || filters.length === 0);
    var filtersRemoved = isPreQueryFiltersEnabled && filters.length === 0 && !isEqual(filtersRef.current, []);

    // If the user returns to the pre-query state by removing the query or by clearing the filters when there isn't a query
    if (isDisplayed && query === '' && (queryRemoved || filtersRemoved)) {
      // Reset the post query api state to typing if debounced search is off, and loading is debounced search is on
      if (preventDebouncedSearch) {
        setPostQueryAPIState(APIStates.Typing);
      } else {
        setPostQueryAPIState(APIStates.Loading);
      }
    }
    queryRef.current = query;
    filtersRef.current = filters;
  }, [query, isDisplayed, setPostQueryAPIState, queryRef, filtersRef, preventDebouncedSearch, filters, isPreQueryFiltersEnabled]);
};
export var usePreQueryInvocation = function usePreQueryInvocation(_ref5) {
  var filters = _ref5.filters,
    isPrefetchingEnabled = _ref5.isPrefetchingEnabled,
    isPreQueryFiltersEnabled = _ref5.isPreQueryFiltersEnabled,
    sectionIds = _ref5.sectionIds,
    preQueryItemSupplier = _ref5.preQueryItemSupplier,
    isDisplayed = _ref5.isDisplayed,
    workspaces = _ref5.workspaces;
  /**
   * Even in case of cache warming we want to track the product state hence we should always pass the decorated supplier. Note: Both decorated and non decorated suppliers have the same signature.
   */

  useEffect(function () {
    if ((!isPreQueryFiltersEnabled || filters.length === 0) && (isPrefetchingEnabled === true || isDisplayed === true) && sectionIds && sectionIds.length > 0) {
      preQueryItemSupplier({
        sectionIds: sectionIds,
        workspaces: workspaces
      }).catch(function (ignore) {});
    }
  }, [isPrefetchingEnabled, isPreQueryFiltersEnabled, isDisplayed, sectionIds, preQueryItemSupplier, workspaces, filters.length]);
};
export var useOnRetryPreQueryInvocation = function useOnRetryPreQueryInvocation(_ref6) {
  var preQueryItemSupplier = _ref6.preQueryItemSupplier,
    onRetry = _ref6.onRetry,
    sectionIds = _ref6.sectionIds,
    workspaces = _ref6.workspaces;
  return useCallback(function () {
    preQueryItemSupplier({
      sectionIds: sectionIds,
      workspaces: workspaces
    });
    onRetry === null || onRetry === void 0 || onRetry();
  }, [onRetry, preQueryItemSupplier, sectionIds, workspaces]);
};
export var useOnRetryPostQueryInvocation = function useOnRetryPostQueryInvocation(_ref7) {
  var postQueryItemSupplier = _ref7.postQueryItemSupplier,
    onRetry = _ref7.onRetry,
    query = _ref7.query,
    analyticsInfo = _ref7.analyticsInfo,
    filters = _ref7.filters,
    sectionIds = _ref7.sectionIds,
    workspaces = _ref7.workspaces;
  return useCallback(function () {
    postQueryItemSupplier({
      query: query,
      analyticsInfo: analyticsInfo,
      sectionIds: sectionIds,
      filters: filters,
      workspaces: workspaces
    });
    onRetry === null || onRetry === void 0 || onRetry();
  }, [postQueryItemSupplier, query, analyticsInfo, sectionIds, filters, workspaces, onRetry]);
};
export var useInvocations = function useInvocations(_ref8) {
  var id = _ref8.id,
    debounceTime = _ref8.debounceTime,
    query = _ref8.query,
    analyticsInfo = _ref8.analyticsInfo,
    filters = _ref8.filters,
    isPrefetchingEnabled = _ref8.isPrefetchingEnabled,
    isPreQueryFiltersEnabled = _ref8.isPreQueryFiltersEnabled,
    workspaces = _ref8.workspaces,
    onRetry = _ref8.onRetry,
    preQueryItemSupplier = _ref8.preQueryItemSupplier,
    postQueryItemSupplier = _ref8.postQueryItemSupplier,
    setPostQueryItems = _ref8.setPostQueryItems,
    setPostQueryAPIState = _ref8.setPostQueryAPIState;
  var _useProductContext = useProductContext(),
    getProduct = _useProductContext.getProduct;
  var product = getProduct(id);
  var isDisplayed = (product === null || product === void 0 ? void 0 : product.isDisplayed) || false;
  var sectionIds = useMemo(function () {
    return (product === null || product === void 0 ? void 0 : product.sectionIds) || [];
  }, [product]);
  var preventDebouncedSearch = !!(product !== null && product !== void 0 && product.showSearchOnEnter);
  var invokePostQuerySearchRef = product === null || product === void 0 ? void 0 : product.invokePostQuerySearchRef;
  var invokePostQueryItemSupplier = useCallback(function () {
    postQueryItemSupplier({
      query: query,
      filters: filters,
      analyticsInfo: analyticsInfo,
      sectionIds: sectionIds,
      workspaces: workspaces
    }).catch(function (ignore) {});
  }, [analyticsInfo, filters, postQueryItemSupplier, query, sectionIds, workspaces]);
  if (invokePostQuerySearchRef) {
    invokePostQuerySearchRef.current = invokePostQueryItemSupplier;
  }
  usePostQueryInvocation({
    isDisplayed: isDisplayed,
    debounceTime: debounceTime,
    postQueryItemSupplier: postQueryItemSupplier,
    query: query,
    analyticsInfo: analyticsInfo,
    filters: filters,
    sectionIds: sectionIds,
    setPostQueryItems: setPostQueryItems,
    setPostQueryAPIState: setPostQueryAPIState,
    workspaces: workspaces,
    preventDebouncedSearch: !!preventDebouncedSearch,
    isPreQueryFiltersEnabled: isPreQueryFiltersEnabled
  });
  usePreQueryInvocation({
    isDisplayed: isDisplayed,
    preQueryItemSupplier: preQueryItemSupplier,
    filters: filters,
    sectionIds: sectionIds,
    isPrefetchingEnabled: isPrefetchingEnabled,
    isPreQueryFiltersEnabled: isPreQueryFiltersEnabled,
    workspaces: workspaces
  });
  var updateStateOnRetryPreQuery = useOnRetryPreQueryInvocation({
    preQueryItemSupplier: preQueryItemSupplier,
    onRetry: onRetry,
    sectionIds: sectionIds,
    isDisplayed: isDisplayed,
    workspaces: workspaces
  });
  var updateStateOnRetryPostQuery = useOnRetryPostQueryInvocation({
    postQueryItemSupplier: postQueryItemSupplier,
    onRetry: onRetry,
    query: query,
    analyticsInfo: analyticsInfo,
    filters: filters,
    sectionIds: sectionIds,
    isDisplayed: isDisplayed,
    workspaces: workspaces
  });
  return {
    onRetryPreQuery: updateStateOnRetryPreQuery,
    onRetryPostQuery: updateStateOnRetryPostQuery
  };
};